import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { Button, Input } from "@jhool-io/fe-components";
import { useNavigate } from "react-router-dom";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSendOtpCode, useVerifyOtpCode } from "../../hooks/auth.mutations";
import AuthWrapper from "../AuthWrapper/AuthWrapper";
import useAuthContext from "../../../../hooks/useAuthContext";
import useToast from "../../../../hooks/useToast";
import {
    getOtpChallengeId,
    getOtpCorrelationId,
    getOtpCooldownEnd,
    setOtpSession,
    clearOtpSession,
} from "../../../../utils/otpSessionStore";
import { USER_DETAILS_QUERY_KEY } from "../../constants/auth.constants";

const OTP_RESEND_COOLDOWN_SECONDS = 120;

function formatCooldownTime(seconds: number): string {
    const minutesPart = Math.floor(seconds / 60);
    const secondsPart = seconds % 60;
    return `${minutesPart}:${secondsPart.toString().padStart(2, "0")}`;
}

function getRemainingCooldownFromEnd(cooldownEnd: number): number {
    const remaining = Math.floor((cooldownEnd - Date.now()) / 1000);
    return remaining > 0 ? remaining : 0;
}

const schema = yup.object({
    code: yup
        .string()
        .required("Verification code is required")
        .matches(/^\d{6}$/, "Code must be a 6-digit number"),
});

export default function OtpVerificationPage() {
    const navigate = useNavigate();
    const { toast } = useToast();
    const { user, logOutUser } = useAuthContext();
    const queryClient = useQueryClient();

    const userId = user?.uid ?? "";
    const userEmail = user?.email ?? "";

    const [challengeId, setChallengeId] = useState<string | undefined>(
        () => getOtpChallengeId(userId) ?? undefined
    );
    const [correlationId, setCorrelationId] = useState<string | undefined>(
        () => getOtpCorrelationId(userId) ?? undefined
    );

    const [cooldownSecondsLeft, setCooldownSecondsLeft] = useState(() =>
        getRemainingCooldownFromEnd(getOtpCooldownEnd(userId))
    );

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<{ code: string }>({
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    const isVerifyingOtp =
        useIsMutating({ mutationKey: ["verify-otp-code"] }) > 0;
    const isSendingOtp = useIsMutating({ mutationKey: ["send-otp-code"] }) > 0;

    const { mutate: sendOtpCode } = useSendOtpCode();
    const { mutate: verifyOtpCode } = useVerifyOtpCode();

    const requestOtpCode = () => {
        if (!userEmail || !userId) {
            return;
        }

        if (cooldownSecondsLeft > 0 || isSendingOtp) {
            return;
        }

        setCooldownSecondsLeft(OTP_RESEND_COOLDOWN_SECONDS);

        Sentry.logger.info("Patient portal OTP resend requested", {
            area: "otp-verification",
            otp_correlation_id: correlationId,
            challenge_id: challengeId,
        });

        sendOtpCode(
            {
                email: userEmail,
                purpose: "login",
                challenge_id: challengeId,
                correlation_id: correlationId,
            },
            {
                onSuccess: (res) => {
                    setChallengeId(res.challenge_id);
                    setCorrelationId(res.otp_correlation_id);
                    setOtpSession(
                        userId,
                        res.challenge_id,
                        res.otp_correlation_id,
                        OTP_RESEND_COOLDOWN_SECONDS
                    );
                    setCooldownSecondsLeft(OTP_RESEND_COOLDOWN_SECONDS);
                    Sentry.logger.info("Patient portal OTP resend completed", {
                        area: "otp-verification",
                        otp_correlation_id: res.otp_correlation_id,
                        challenge_id: res.challenge_id,
                    });
                    toast({
                        mode: "success",
                        message: "Verification code sent to your email.",
                    });
                },
                onError: (err) => {
                    Sentry.logger.warn("Patient portal OTP resend failed", {
                        area: "otp-verification",
                        otp_correlation_id: correlationId,
                        challenge_id: challengeId,
                        response_status: err.response?.status ?? "none",
                    });
                    if (err.response?.status === 429) {
                        setOtpSession(
                            userId,
                            challengeId ?? "",
                            correlationId ?? "",
                            OTP_RESEND_COOLDOWN_SECONDS
                        );
                        setCooldownSecondsLeft(OTP_RESEND_COOLDOWN_SECONDS);
                    } else {
                        setCooldownSecondsLeft(0);
                    }

                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Unable to send OTP right now. Please try again.",
                    });
                },
            }
        );
    };

    // Countdown timer
    useEffect(() => {
        let timer: number | undefined;

        if (cooldownSecondsLeft > 0) {
            timer = window.setInterval(() => {
                setCooldownSecondsLeft((prev) => {
                    const next = Math.max(prev - 1, 0);
                    return next;
                });
            }, 1000);
        }

        return () => {
            if (timer) {
                window.clearInterval(timer);
            }
        };
    }, [cooldownSecondsLeft]);

    const onSubmit = (payload: { code: string }) => {
        Sentry.logger.info("Patient portal OTP verification submitted", {
            area: "otp-verification",
            otp_correlation_id: correlationId,
            challenge_id: challengeId,
        });

        verifyOtpCode(
            {
                code: payload.code,
                email: userEmail,
                purpose: "login",
                challenge_id: challengeId,
                correlation_id: correlationId,
            },
            {
                onSuccess: (res) => {
                    if (res.verified) {
                        Sentry.logger.info(
                            "Patient portal OTP verification succeeded",
                            {
                                area: "otp-verification",
                                otp_correlation_id: correlationId,
                                challenge_id: challengeId,
                            }
                        );
                        clearOtpSession(userId);
                        queryClient.removeQueries({
                            queryKey: USER_DETAILS_QUERY_KEY,
                        });
                        navigate("/", { replace: true });
                    } else {
                        Sentry.logger.warn(
                            "Patient portal OTP verification failed",
                            {
                                area: "otp-verification",
                                otp_correlation_id: correlationId,
                                challenge_id: challengeId,
                            }
                        );
                        toast({
                            mode: "error",
                            message: "Invalid or expired OTP code.",
                        });
                    }
                },
                onError: (err) => {
                    Sentry.logger.error(
                        "Patient portal OTP verification errored",
                        {
                            area: "otp-verification",
                            otp_correlation_id: correlationId,
                            challenge_id: challengeId,
                            response_status: err.response?.status ?? "none",
                        }
                    );
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.verified === false
                                ? "Invalid or expired OTP code."
                                : "Unable to verify OTP code. Please try again.",
                    });
                },
            }
        );
    };

    let resendButtonText = "Get code";
    if (isSendingOtp) {
        resendButtonText = "Sending...";
    } else if (cooldownSecondsLeft > 0) {
        resendButtonText = `Resend in ${formatCooldownTime(cooldownSecondsLeft)}`;
    } else if (challengeId) {
        resendButtonText = "Get new code";
    }

    return (
        <AuthWrapper>
            <h2 className="text-xxl font-bold leading-[32px] mb-4">
                Verification code required
            </h2>
            <p className="text-[#525866] font-normal text-sm mb-24">
                Enter the code sent to {userEmail || "your email"} to continue.
            </p>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Input
                    {...register("code")}
                    label="Verification code"
                    placeholder="Enter code"
                    isRequired
                />
                {errors.code?.message ? (
                    <p className="text-sm text-[#D92D20] mt-8">
                        {errors.code.message}
                    </p>
                ) : null}
                <Button
                    className="w-full mt-24"
                    type="submit"
                    size="medium"
                    disabled={isVerifyingOtp}
                >
                    {isVerifyingOtp ? "Verifying..." : "Verify code"}
                </Button>

                <div className="flex justify-center items-center mt-16 text-sm text-[#525866]">
                    Didn&apos;t get a code?{" "}
                    <Button
                        type="button"
                        variant="normal"
                        size="medium"
                        className="font-medium text-primary disabled:text-[#98A2B3] p-2"
                        disabled={isSendingOtp || cooldownSecondsLeft > 0}
                        onClick={requestOtpCode}
                    >
                        {resendButtonText}
                    </Button>
                </div>

                <Button
                    type="button"
                    variant="normal"
                    size="medium"
                    className="text-sm font-medium text-primary w-full mt-8"
                    onClick={async () => {
                        clearOtpSession(userId);
                        await logOutUser();
                    }}
                >
                    Back to Login
                </Button>
            </form>
        </AuthWrapper>
    );
}
